/**
 * PATH: packages/ui/src/kanban/components/card-detail/activity.tsx
 * FILE_ID: UI-KANBAN-CMP-111
 * PURPOSE: Audit-trail humaniser + ActivityRow renderer.
 * WHY: Split from the 4,176-line CardDetailDrawer monolith (UAT P5).
 *      Verbatim move - behaviour unchanged; the host re-exports the
 *      public surface so existing imports keep working.
 */

import { type ReactNode, useCallback, useMemo, useState } from 'react'
import {
  Archive, Calendar, CheckSquare, ChevronUp, Clock, CornerDownRight, ExternalLink, FileText, Flag,
  Loader2, Mail, Merge, MessageSquare, Network, Paperclip, Pin, PinOff, Plus,
  RotateCcw,
  ShieldAlert, Tag as TagIcon, Trash2, User, UserPlus, X,
} from 'lucide-react'
import type { KanbanApi, KanbanEmailAdapter, KanbanMember } from '../../api'
import { KanbanCardConflictError } from '../../api'
import { MentionTextarea } from '../MentionTextarea'
import { PeoplePicker } from '../PeoplePicker'
import type {
  AuditLogEntry,
  Card, CardChecklistItemT, CardChecklistT, CardCommentT,
  CardStatus, Lane, Tag,
} from '../../types'
import { KanbanAvatar } from '../KanbanAvatar'
import { EmailLinkTickCross } from '../EmailLinkTickCross'
import { ChecklistItemContextMenu } from '../ChecklistItemContextMenu'
import { SpeechMicButton } from '../SpeechMicButton'
import type { TrelloCardDetail } from '../../types'
import { formatDateTime } from './shared'
import { formatDmy } from '../../utils/localDate'
import { getCardAuditRevertPlan } from '../../auditRevert'


// =============================================================================
// _humaniseAuditEvent — verb-first, context-aware label for one audit row
// =============================================================================
//
// Replaces a ternary chain. Centralises the special-cases (esp.
// `status_changed` where archive vs. restore vs. other transitions
// deserve different verbs in the trail). Keeps the row-rendering JSX
// readable and gives the test suite one humanisation function to pin
// instead of duplicating the case-list inside each test.
//
// Contract: pure function over `entry`. No IO, no React.
//
//   _humaniseAuditEvent({event: 'status_changed', before: 'OPEN', after: 'ARCHIVED'})
//     → 'Archived'
//   _humaniseAuditEvent({event: 'status_changed', before: 'ARCHIVED', after: 'OPEN'})
//     → 'Restored'
//   _humaniseAuditEvent({event: 'status_changed', before: 'OPEN', after: 'BLOCKED'})
//     → 'Status: OPEN → BLOCKED'
//   _humaniseAuditEvent({event: 'priority_changed', before: 1, after: 3})
//     → 'Priority: 1 → 3'
//   _humaniseAuditEvent({event: 'due_date_changed', before: '2026-06-01', after: '2026-07-01'})
//     → 'Due date changed'
//   _humaniseAuditEvent({event: 'tag_attached'})
//     → 'Tagged'
//   _humaniseAuditEvent({event: 'foo_bar'})
//     → 'foo_bar'  // fallback — never crashes the row

export function _humaniseAuditEvent(entry: AuditLogEntry): string {
  switch (entry.event) {
    case 'lane_changed':
      return 'Moved'
    case 'card_reordered':
      return 'Reordered'
    case 'card_assignees_set':
      return 'Assignees changed'
    case 'tag_attached':
      return 'Tagged'
    case 'tag_detached':
      return 'Untagged'
    case 'assignee_added':
      return 'Assigned'
    case 'assignee_removed':
      return 'Unassigned'
    case 'comment_added':
      return 'Commented'
    case 'status_changed': {
      const before = typeof entry.before === 'string' ? entry.before : ''
      const after = typeof entry.after === 'string' ? entry.after : ''
      if (after === 'ARCHIVED' && before !== 'ARCHIVED') return 'Archived'
      if (before === 'ARCHIVED' && after !== 'ARCHIVED') return 'Restored'
      if (after === 'BLOCKED' && before !== 'BLOCKED') return 'Marked blocked'
      if (before === 'BLOCKED' && after !== 'BLOCKED') return 'Unblocked'
      if (after === 'DONE' && before !== 'DONE') return 'Marked done'
      if (before && after) return `Status: ${before} → ${after}`
      return 'Status changed'
    }
    case 'priority_changed': {
      const before = entry.before
      const after = entry.after
      if (typeof before === 'number' && typeof after === 'number') {
        return `Priority: ${before} → ${after}`
      }
      return 'Priority changed'
    }
    case 'due_date_changed':
      return entry.before === null || entry.before === undefined
        ? 'Due date set'
        : entry.after === null
        ? 'Due date cleared'
        : 'Due date changed'
    // BUG-2026-08-04-KANBAN-CHECKLIST-DATE-ACTIVITY-MISSING
    case 'checklist_created':
      return 'Checklist added'
    case 'checklist_deleted':
      return 'Checklist removed'
    // FEAT-2026-06-29-KANBAN-ACTION-DATE — the primary "next action" date
    // + its note in the activity trail.
    case 'action_date_changed':
      return entry.before === null || entry.before === undefined
        ? 'Action date set'
        : entry.after === null
        ? 'Action date cleared'
        : 'Action date changed'
    case 'action_note_changed':
      return 'Action note changed'
    case 'start_date_changed':
      return 'Start date changed'
    case 'blocked_reason_changed':
      return 'Blocked reason changed'
    case 'sensitive_changed':
      return entry.after === true ? 'Marked sensitive' : 'Unmarked sensitive'
    case 'snoozed_until_changed':
      return entry.before === null || entry.before === undefined
        ? 'Snoozed'
        : entry.after === null
        ? 'Snooze cleared'
        : 'Snooze rescheduled'
    // FEAT-2026-06-30-KANBAN-MERGE-UX — thread-merge audit rows. The
    // keeper gains a `merged_thread_into_card` row per loser absorbed;
    // each archived loser gains a single `merged_into_card` row. Both
    // store the OTHER card's id (see merge_threads.merge_thread_group)
    // so ActivityRow can deep-link to it.
    case 'merged_thread_into_card':
      return 'Merged a card in'
    // BUG-2026-07-07-KANBAN-DUP-MERGE-ROW — the background duplicate-
    // dedupe job (merge_duplicates.merge_card_group) stamps the keeper
    // with `merged_duplicate_card` (NOT `merged_thread_into_card`). It
    // was never in this switch, so a keeper that absorbed a duplicate
    // rendered a raw "merged_duplicate_card" row with no label and no
    // Open/Undo affordance — the un-actionable "second merge entry" the
    // operator saw. It carries the SAME provenance shape (merged_card_id
    // + primary_email_moved + related_emails_moved), so it links + undoes
    // exactly like a thread merge.
    case 'merged_duplicate_card':
      return 'Merged a duplicate in'
    case 'merged_into_card':
      return 'Merged into another card'
    // FEAT-2026-07-06-KANBAN-UNMERGE — reversal trail rows written by the
    // unmerge endpoint on both sides of a restored merge.
    case 'card_unmerged':
      return 'Un-merged a card'
    case 'restored_from_merge':
      return 'Restored from a merge'
    // FEAT-2026-07-06-KANBAN-REVERT-LIFECYCLE — appended by the
    // revert-audit endpoint so the reversal itself is observable.
    case 'action_reverted':
      return 'Reverted an action'
    // Allocation lifecycle (email_kanban.services.allocation_audit) —
    // was falling through as raw snake_case in Activity.
    case 'alloc_created':
      return 'Email linked'
    case 'alloc_ai_confirmed':
      return 'AI placement accepted'
    case 'alloc_ai_rejected':
      return 'AI placement rejected'
    case 'alloc_revoked':
      return 'Email unlinked'
    case 'alloc_moved':
      return 'Email moved'
    case 'alloc_operator_accepted':
      return 'Placement confirmed'
    default:
      return entry.event
  }
}

// =============================================================================
// _auditDetailText — the human "what changed" detail for a row, when the
// short verb-label (`_humaniseAuditEvent`) alone hides the values.
// =============================================================================
//
// Operator 2026-06-30: *"Activity like due date changed is not clearly
// visible, maybe when I hover you can show me the entire message?"* — the
// verb-only label ("Due date changed") never showed the before→after
// dates. This returns a "01/06/2026 → 01/07/2026" style detail for the
// date-type events (the verb-label already embeds before→after for
// status / priority), which the row renders as a visible second line AND
// folds into the row's hover `title`. Pure; returns null when there's no
// useful before/after pair to show.
export function _auditDetailText(entry: AuditLogEntry): string | null {
  const fmt = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—'
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return formatDmy(v) || v
    return String(v)
  }
  switch (entry.event) {
    case 'due_date_changed':
    case 'action_date_changed':
    case 'start_date_changed':
    case 'snoozed_until_changed': {
      const hasPair = 'before' in entry || 'after' in entry
      return hasPair ? `${fmt(entry.before)} → ${fmt(entry.after)}` : null
    }
    case 'checklist_created':
    case 'checklist_deleted': {
      const title = typeof entry.title === 'string' ? entry.title.trim() : ''
      return title || null
    }
    default:
      return null
  }
}

// =============================================================================
// ActivityRow — a single audit-log row + Revert button when reversible
// =============================================================================
//
// Operator request 2026-05-15: *"Add the ability to undo last action.. this
// should be part of the tracing activity, with just revert and it reverts
// that action.."*
//
// The Activity panel renders rows for `card.audit_log_json` entries. For
// rows whose event kind has both before+after captured (`lane_changed`,
// `card_reordered`, `card_assignees_set`), a small Revert button appears
// on the right of the row. Clicking it fires the inverse mutation through
// the existing api surface (no new backend endpoint required) and seeds
// the parent's cache via `onReverted(updatedCard)`.
//
// The policy of "which rows are reversible" lives in `auditRevert.ts` as
// a pure I→O utility — this component is a thin presentation layer over
// the plan that utility returns. Adding new reversible event kinds is a
// one-file change in `auditRevert.ts` plus optionally a label tweak in the
// `niceEvent` map above; no JSX edits needed here.

/**
 * Props for the audit-log row + Revert button. Exported alongside the
 * component so consumers (and tests) get the same typed shape.
 */
export interface ActivityRowProps {
  entry: AuditLogEntry
  niceEvent: string
  fromName: string | null
  toName: string | null
  /** Display name of who performed the action (resolved from `entry.by`
   *  by the drawer). `null` when the backend didn't record an actor
   *  (legacy rows / system events). */
  actorName?: string | null
  card: Card
  api: KanbanApi
  laneNameById: Map<string, string>
  onReverted: (card: Card) => void
  /** FEAT-2026-06-30-KANBAN-MERGE-UX — open another card in the drawer.
   *  Wired by the host (BoardRightRail → api.getCard → setDetailCard).
   *  When present, merge audit rows render a deep-link to the keeper
   *  (from a loser row) or the archived loser (from a keeper row). When
   *  omitted the row degrades to a plain, non-clickable label. May return a
   *  promise so the row can await the open + degrade on a failed fetch. */
  onOpenCardId?: (cardId: string) => void | Promise<void>
}

/**
 * Exported (rather than left private) so the
 * `auditRevert.row.test.tsx` regression suite can render it in
 * isolation against a stubbed KanbanApi — mounting the full
 * CardDetailDrawer just to test one row would require stubbing 20+
 * unrelated subsystems (members / tags / checklists / email adapter /
 * queryClient / …). The component itself is reusable: any future
 * activity surface (job-history rail, board-wide activity feed) can
 * render the same row with the same Revert wiring.
 */
export function ActivityRow({
  entry,
  niceEvent,
  fromName,
  toName,
  actorName,
  card,
  api,
  laneNameById,
  onReverted,
  onOpenCardId,
}: ActivityRowProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // FEAT-2026-07-06-KANBAN-UNMERGE — separate in-flight state for the
  // "Undo merge" button so it can't be confused with the Revert spinner.
  const [unmergeBusy, setUnmergeBusy] = useState(false)
  const [unmergeError, setUnmergeError] = useState<string | null>(null)
  // BUG-2026-07-07-KANBAN-CARD-404-TOAST-SPAM — graceful degrade. When the
  // linked card can't be fetched (hard-deleted loser, or a sub-resource
  // 404 the backend still filters), opening it fails. Track that per-row so
  // we replace the "Open …" button with an inline "card unavailable" note
  // instead of leaving a dead button the operator clicks again and again
  // (each click = another 404 toast).
  const [openBusy, setOpenBusy] = useState(false)
  const [openUnavailable, setOpenUnavailable] = useState(false)

  // FEAT-2026-07-06-KANBAN-REVERT-LIFECYCLE — a row the backend has
  // already flagged as reverted (via /revert-audit/) is no longer
  // revertible: hide the Revert button + show a "Reverted" chip instead,
  // and derive that from the persisted flag so it survives a reload.
  const isReverted = entry.reverted === true
  // A merge row that has since been undone must not offer "Undo merge"
  // again (idempotent server-side, but the affordance should vanish).
  const isUnmerged = entry.unmerged === true

  // FEAT-2026-06-30-KANBAN-MERGE-UX — resolve the deep-link target id from
  // the merge audit row. The loser stores `merged_into_id` (→ keeper); the
  // keeper stores `merged_card_id` (→ archived loser). Both written by
  // merge_threads.merge_thread_group.
  // Keeper-side merge provenance events both store the absorbed loser's
  // id under `merged_card_id`: `merged_thread_into_card` (thread / manual
  // merge) and `merged_duplicate_card` (background dedupe job). The
  // loser-side `merged_into_card` stores the keeper under `merged_into_id`.
  const isKeeperMergeRow =
    entry.event === 'merged_thread_into_card' ||
    entry.event === 'merged_duplicate_card'
  const mergeTargetId =
    entry.event === 'merged_into_card'
      ? (typeof entry.merged_into_id === 'string' ? entry.merged_into_id : null)
      : isKeeperMergeRow
      ? (typeof entry.merged_card_id === 'string' ? entry.merged_card_id : null)
      : null
  const mergeLinkLabel =
    entry.event === 'merged_into_card' ? 'Open keeper card' : 'Open merged-in card'

  // FEAT-2026-07-06-KANBAN-UNMERGE — resolve (keeperId, loserId) for the
  // "Undo merge" button from whichever side of the merge this row sits on.
  // Keeper row (`merged_thread_into_card`): this card IS the keeper, the
  // target is the loser to restore. Loser row (`merged_into_card`): the
  // target is the keeper, this card is the loser.
  const unmergePair =
    mergeTargetId === null
      ? null
      : isKeeperMergeRow
      ? { keeperId: card.id, loserId: mergeTargetId }
      : entry.event === 'merged_into_card'
      ? { keeperId: mergeTargetId, loserId: card.id }
      : null

  // "Due date changed" → "01/06/2026 → 01/07/2026" so the row shows WHAT
  // changed without opening anything (operator 2026-06-30).
  const detail = _auditDetailText(entry)

  // Plan is pure / cheap — recompute on every render is fine and keeps
  // it in sync with the latest card / api references. The execute()
  // closure inside the plan is what actually fires the inverse on click.
  const plan = useMemo(
    () =>
      getCardAuditRevertPlan({
        entry,
        card,
        api,
        resolveLaneName: (id) => laneNameById.get(id),
      }),
    [entry, card, api, laneNameById],
  )

  const handleRevert = useCallback(async () => {
    if (!plan.canRevert || busy) return
    setBusy(true)
    setError(null)
    try {
      // 1. Fire the inverse mutation (existing api surface).
      const updated = await plan.execute()
      // 2. FEAT-2026-07-06-KANBAN-REVERT-LIFECYCLE — record the reversal so
      //    the ORIGINAL row hides its Revert button (persisted `reverted`
      //    flag) and the trail gains an `action_reverted` entry. The
      //    returned card carries both changes; prefer it. Best-effort: if
      //    the host predates the endpoint (or it fails) we still bubble the
      //    inverse-mutated card so the revert itself isn't lost.
      let finalCard = updated
      if (api.markAuditReverted && typeof entry.at === 'string' && entry.event) {
        try {
          finalCard = await api.markAuditReverted(card.id, {
            at: entry.at,
            event: entry.event,
          })
        } catch {
          // keep `updated` — local-only revert, button re-shows on reload
        }
      }
      onReverted(finalCard)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Revert failed')
    } finally {
      setBusy(false)
    }
  }, [plan, busy, onReverted, api, card.id, entry.at, entry.event])

  const handleUnmerge = useCallback(async () => {
    if (!unmergePair || unmergeBusy || !api.unmergeCards) return
    setUnmergeBusy(true)
    setUnmergeError(null)
    try {
      const res = await api.unmergeCards(unmergePair.keeperId, [unmergePair.loserId])
      // The refreshed keeper carries the `unmerged`-flagged provenance rows
      // (so this button vanishes) and the decremented merged count. Bubble
      // it so the drawer + board cache refresh and the restored loser
      // re-appears on its lane.
      if (res.card) onReverted(res.card)
    } catch (err) {
      setUnmergeError(err instanceof Error ? err.message : 'Undo merge failed')
    } finally {
      setUnmergeBusy(false)
    }
  }, [unmergePair, unmergeBusy, api, onReverted])

  const handleOpenTarget = useCallback(async () => {
    if (!onOpenCardId || !mergeTargetId || openBusy) return
    setOpenBusy(true)
    try {
      // `onOpenCardId` may be sync (void) or async (the right-rail's
      // cycle-aware handler returns a promise that rejects on a failed
      // fetch). Await either so a rejection degrades this row.
      await onOpenCardId(mergeTargetId)
      setOpenUnavailable(false)
    } catch {
      setOpenUnavailable(true)
    } finally {
      setOpenBusy(false)
    }
  }, [onOpenCardId, mergeTargetId, openBusy])

  return (
    <li className="rounded border border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 px-2 py-1.5 text-[11px] text-neutral-700 dark:text-neutral-300">
      <div className="flex items-center justify-between gap-2">
        <span
          className="min-w-0 flex-1 truncate font-semibold"
          title={detail ? `${niceEvent} — ${detail}` : niceEvent}
        >
          {niceEvent}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {/* FEAT-2026-07-06-KANBAN-REVERT-LIFECYCLE — once reverted the row
              is no longer revertible; a static chip replaces the button. */}
          {isReverted && (
            <span
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 dark:text-neutral-500"
              title="This action was reverted"
            >
              <RotateCcw className="h-3 w-3" aria-hidden />
              Reverted
            </span>
          )}
          {plan.canRevert && !isReverted && (
            <button
              type="button"
              onClick={() => void handleRevert()}
              disabled={busy}
              title={plan.describe}
              aria-label={`${plan.label}: ${plan.describe}`}
              className={[
                'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition',
                busy
                  ? 'cursor-not-allowed text-neutral-400 dark:text-neutral-600'
                  : 'text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/40',
              ].join(' ')}
            >
              {busy ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <RotateCcw className="h-3 w-3" aria-hidden />
              )}
              {busy ? 'Reverting…' : 'Revert'}
            </button>
          )}
          <span className="font-mono text-[10px] text-neutral-400">
            {formatDateTime(entry.at)}
          </span>
        </div>
      </div>
      {/* WHO did it — its own line so it's never truncated by the event
          label (operator 2026-06-30: "the name is there but hardly
          visible"). A filled user chip + readable name reads as a person
          at a glance instead of a faint grey suffix. */}
      {actorName && (
        <div className="mt-1 flex items-center gap-1.5">
          <span
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300"
            aria-hidden
          >
            <User className="h-2.5 w-2.5" />
          </span>
          <span className="truncate text-[11px] font-medium text-neutral-700 dark:text-neutral-200">
            {actorName}
          </span>
        </div>
      )}
      {fromName !== null && toName !== null && (
        <div className="mt-0.5 text-[10.5px] text-neutral-500">
          {fromName} → {toName}
        </div>
      )}
      {/* WHAT changed — visible before→after for date-type events so the
          operator doesn't have to hover (operator 2026-06-30). */}
      {detail && (
        <div className="mt-0.5 break-words text-[10.5px] text-neutral-500">{detail}</div>
      )}
      {/* FEAT-2026-06-30-KANBAN-MERGE-UX — deep-link the merge row to the
          other card. The keeper→loser target is archived; the host's
          api.getCard returns it by id regardless of status. */}
      {mergeTargetId && (onOpenCardId || (api.unmergeCards && unmergePair)) && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {/* BUG-2026-07-07-KANBAN-CARD-404-TOAST-SPAM — once the target
              proved unresolvable, swap the button for a static note so the
              operator can't keep re-firing the 404. */}
          {onOpenCardId && openUnavailable ? (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] font-medium text-neutral-400 dark:text-neutral-500"
              title="The linked card is no longer available"
            >
              <Merge className="h-3 w-3" aria-hidden />
              Card unavailable
            </span>
          ) : onOpenCardId ? (
            <button
              type="button"
              onClick={() => void handleOpenTarget()}
              disabled={openBusy}
              className={[
                'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium transition',
                openBusy
                  ? 'cursor-not-allowed text-neutral-400 dark:text-neutral-600'
                  : 'text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/40',
              ].join(' ')}
              title={mergeLinkLabel}
            >
              {openBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <Merge className="h-3 w-3" aria-hidden />
              )}
              {mergeLinkLabel}
            </button>
          ) : null}
          {/* FEAT-2026-07-06-KANBAN-UNMERGE — reverse the merge from the row
              that recorded it. Hidden once already undone (`unmerged`). */}
          {api.unmergeCards && unmergePair && !isUnmerged && (
            <button
              type="button"
              onClick={() => void handleUnmerge()}
              disabled={unmergeBusy}
              title="Restore the merged card(s) and detach their emails"
              aria-label="Undo merge"
              className={[
                'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium transition',
                unmergeBusy
                  ? 'cursor-not-allowed text-neutral-400 dark:text-neutral-600'
                  : 'text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/40',
              ].join(' ')}
            >
              {unmergeBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <RotateCcw className="h-3 w-3" aria-hidden />
              )}
              {unmergeBusy ? 'Undoing…' : 'Undo merge'}
            </button>
          )}
          {isUnmerged && (
            <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-neutral-400 dark:text-neutral-500">
              Merge undone
            </span>
          )}
        </div>
      )}
      {unmergeError && (
        <div
          role="alert"
          className="mt-0.5 text-[10.5px] text-red-600 dark:text-red-400"
        >
          {unmergeError}
        </div>
      )}
      {/* `reason` arrives via AuditLogEntry's index signature, so it's
          `unknown` — Boolean() keeps the guard while String() renders. */}
      {Boolean(entry.reason) && (
        <div className="mt-0.5 text-[10.5px] text-neutral-500 italic">
          {String(entry.reason)}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="mt-0.5 text-[10.5px] text-red-600 dark:text-red-400"
        >
          {error}
        </div>
      )}
    </li>
  )
}
